/* ============================================================
   GRANDEUR Tilt — Shopify section behaviour
   Adapted from the static app.js. Shopify changes:
   • No colourway theme-swap: each colour is its own product, so the
     colourway cards are <a> links to sibling product pages. The page
     renders its colour server-side via data-theme on .tilt-world.
   • Add-to-cart posts to Shopify's /cart/add.js (AJAX) and updates the
     sticky bar + a cart count.
   • Everything is scoped/guarded so it's safe to run inside a theme.
   ============================================================ */
(function () {
  "use strict";
  var prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // sri = SHA-384 hash of the pinned CDN file (Subresource Integrity): the browser
  // refuses to run the script if the CDN response doesn't match, and the .catch
  // fallback degrades to the CSS-only experience — no unverified third-party code
  // ever executes on the storefront.
  function loadScript(src, sri){return new Promise(function(res,rej){var s=document.createElement("script");s.src=src;s.async=true;if(sri){s.integrity=sri;s.crossOrigin="anonymous";}s.onload=res;s.onerror=rej;document.head.appendChild(s);});}

  /* ---------- Scroll progress ---------- */
  function initProgress(){
    var bar = document.getElementById("tiltProgress");
    if(!bar || bar.dataset.tiltBound) return;   // idempotent across editor re-inits
    bar.dataset.tiltBound = "1";
    var t=false;
    function on(){ if(t)return; t=true; requestAnimationFrame(function(){
      var y = window.scrollY||window.pageYOffset;
      var max = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.width = (max>0?(y/max)*100:0)+"%"; t=false;
    });}
    window.addEventListener("scroll",on,{passive:true}); on();
  }

  /* ---------- Smooth anchor scrolling ---------- */
  function initAnchors(){
    document.querySelectorAll('.tilt-world a[href^="#"]').forEach(function(a){
      a.addEventListener("click",function(e){
        var id = a.getAttribute("href");
        if(id === "#"){ e.preventDefault(); return; }
        var target = document.querySelector(id);
        if(!target) return;
        e.preventDefault();
        if(window.__tiltLenis) window.__tiltLenis.scrollTo(target,{offset:0,duration:1.2});
        else target.scrollIntoView({behavior: prefersReduced ? "auto" : "smooth"});
      });
    });
  }

  /* ---------- GSAP: card-stack recede + reveal + tilt word ---------- */
  function initGSAP(lenis){
    if(prefersReduced || typeof gsap==="undefined" || typeof ScrollTrigger==="undefined") return false;
    gsap.registerPlugin(ScrollTrigger);
    if(lenis){ lenis.on("scroll",ScrollTrigger.update); gsap.ticker.add(function(t){lenis.raf(t*1000);}); gsap.ticker.lagSmoothing(0); }

    var scenes = gsap.utils.toArray(".tilt-world .scene");
    scenes.forEach(function(scene,i){
      var card = scene.querySelector(".scene__card");
      var img  = scene.querySelector(".scene__media img");
      var info = scene.querySelectorAll(".scene__info > *");
      if(card && i < scenes.length-1){
        gsap.set(card,{transformOrigin:"50% 0%",backfaceVisibility:"hidden"});
        gsap.to(card,{scale:0.94,ease:"none",scrollTrigger:{trigger:scene,start:"top top",end:"bottom top",scrub:true}});
      }
      if(img){
        gsap.fromTo(img,{scale:1.08,yPercent:4},{scale:1,yPercent:0,ease:"power2.out",duration:1,scrollTrigger:{trigger:scene,start:"top 80%",end:"top 30%",scrub:true}});
      }
      if(info.length){
        gsap.from(info,{opacity:0,y:30,filter:"blur(6px)",duration:0.6,stagger:0.07,ease:"power2.out",scrollTrigger:{trigger:scene,start:"top 55%"}});
      }
    });

    var tiltWord = document.querySelector(".tilt-word");
    if(tiltWord){
      gsap.fromTo(tiltWord,{rotate:0},{rotate:24,ease:"none",scrollTrigger:{trigger:tiltWord.closest(".scene"),start:"top top",end:"bottom top",scrub:true}});
    }
    ScrollTrigger.refresh();
    return !!lenis;
  }

  /* ---------- Depth-card parallax (cards are <a> links) ---------- */
  function initDepthParallax(){
    if(prefersReduced) return;
    document.querySelectorAll(".tilt-world .depth-card").forEach(function(card){
      var layers = card.querySelectorAll(".depth-card__layer");
      card.addEventListener("pointermove",function(e){
        if(e.pointerType && e.pointerType !== "mouse") return;
        var r = card.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width  - 0.5;
        var y = (e.clientY - r.top)  / r.height - 0.5;
        card.style.setProperty("--rx",(y*-10)+"deg");
        card.style.setProperty("--ry",(x* 12)+"deg");
        layers.forEach(function(l,i){ var d=(i+1)*8; l.style.transform="translate("+(x*d)+"px,"+(y*d)+"px) scale(1.06)"; });
      });
      card.addEventListener("pointerleave",function(){
        card.style.setProperty("--rx","0deg");
        card.style.setProperty("--ry","0deg");
        layers.forEach(function(l){ l.style.transform=""; });
      });
    });
  }

  /* ---------- Colourway nav images (deferred) ----------
     The 5 sibling-product watch renders are CSS backgrounds (can't lazy-load), so the
     snippet emits them as data-bg and we apply them only when the colourways section
     approaches the viewport — several hundred KB off the critical path. */
  function initColourwayImages(){
    var layers = document.querySelectorAll(".tilt-world .depth-card__layer--watch[data-bg]");
    if(!layers.length) return;
    function applyAll(){ layers.forEach(function(l){ if(!l.style.backgroundImage) l.style.backgroundImage = "url(" + l.getAttribute("data-bg") + ")"; }); }
    var section = document.getElementById("tilt-colourways");
    // collection grid (no #tilt-colourways) or no IO support → load immediately
    if(!section || !("IntersectionObserver" in window)){ applyAll(); return; }
    var io = new IntersectionObserver(function(ents){
      if(ents.some(function(e){ return e.isIntersecting; })){ applyAll(); io.disconnect(); }
    },{ rootMargin: "150%" });   // start fetching ~1.5 screens early — ready before arrival
    io.observe(section);
  }

  /* ---------- Shopify add-to-cart (AJAX) ---------- */
  function initCart(){
    // class-scoped + per-form: survives duplicate Tilt sections on one template,
    // where duplicated element ids would leave the second form unbound
    document.querySelectorAll(".tilt-world .cartbar__form").forEach(function(form){
      if(form.dataset.tiltBound) return;   // idempotent (editor re-runs boot on section:load)
      form.dataset.tiltBound = "1";
      bindCartForm(form);
    });
  }
  function bindCartForm(form){
    var btn = form.querySelector(".cartbar__btn");
    var label = btn && btn.querySelector(".cartbar__label");
    var countEl = form.querySelector(".cartbar__count");
    var live = form.parentElement ? form.parentElement.querySelector(".cartbar__live") : null;
    var resetTimer;

    // Impact's cart UI lives in web components (<cart-count> in the header section,
    // <cart-drawer> in its own section). Their section ids carry store-specific
    // numbers, so read them from the DOM rather than hardcoding.
    function sectionIdEndingWith(suffix){
      var el = document.querySelector('[id^="shopify-section-"][id$="'+suffix+'"]');
      return el ? el.id.replace("shopify-section-","") : null;
    }
    var headerId = sectionIdEndingWith("__header");
    var drawerId = sectionIdEndingWith("__cart-drawer");

    function setOwnCount(n){ if(countEl){ countEl.hidden = n<=0; countEl.textContent = n; } }
    var refreshing=false;
    function refreshOwnCount(){
      if(refreshing) return; refreshing=true;
      fetch("/cart.js",{headers:{"Accept":"application/json"},cache:"no-store"})
        .then(function(r){return r.json();})
        .then(function(c){ setOwnCount(c.item_count); })
        .catch(function(){})
        .then(function(){ refreshing=false; });
    }
    refreshOwnCount();

    // ---- keep the bar's count in sync when the cart changes ANYWHERE ----
    // (theme remove buttons, quantity changes, the cart page, another tab, etc.)
    // 1) listen for the cart events Impact / Dawn-style themes broadcast
    ["cart:refresh","cart:updated","cart:change","cart:build","cart-items:updated"].forEach(function(name){
      document.addEventListener(name, refreshOwnCount);
      document.documentElement.addEventListener(name, refreshOwnCount);
    });
    // 2) catch ALL cart writes theme-agnostically by wrapping fetch: any POST to
    //    /cart/change|update|add|clear means the cart changed → re-read our count.
    if(window.fetch && !window.__tiltCartHook){
      window.__tiltCartHook = true;
      var _fetch = window.fetch;
      window.fetch = function(input, init){
        var url = (typeof input === "string") ? input : (input && input.url) || "";
        var p = _fetch.apply(this, arguments);
        if(/\/cart\/(change|update|add|clear)(\.js)?/i.test(url)){
          p.then(function(){ setTimeout(refreshOwnCount, 60); }).catch(function(){});
        }
        return p;
      };
    }
    // 3) re-check when the page is shown again (cart edited on the cart page / bfcache)
    document.addEventListener("visibilitychange",function(){ if(!document.hidden) refreshOwnCount(); });
    window.addEventListener("pageshow",refreshOwnCount);

    // swap a freshly-rendered fragment from a returned section into the live DOM.
    // updateInPlace: copy fresh innerHTML + classes WITHOUT replacing the element,
    // so we don't detach the theme's own <cart-count>/<cart-drawer> references.
    function updateInPlace(html, selector){
      try{
        var doc = new DOMParser().parseFromString(html,"text/html");
        var fresh = doc.querySelector(selector), live = document.querySelector(selector);
        if(fresh && live){ live.innerHTML = fresh.innerHTML; live.className = fresh.className; return true; }
      }catch(e){}
      return false;
    }

    form.addEventListener("submit",function(e){
      e.preventDefault();
      if(!btn || btn.disabled) return;
      var data = new FormData(form);
      // ask Shopify to re-render Impact's header + cart-drawer with the updated cart
      var ids = [headerId, drawerId].filter(Boolean);
      if(ids.length){ data.append("sections", ids.join(",")); data.append("sections_url", location.pathname); }

      var orig = label ? label.textContent : "";
      if(label) label.textContent = "Adding…";
      if(btn) btn.disabled = true;

      fetch("/cart/add.js",{method:"POST",headers:{"Accept":"application/json","X-Requested-With":"XMLHttpRequest"},body:data})
        .then(function(r){ if(!r.ok) return r.json().then(function(j){ throw j; }); return r.json(); })
        .then(function(res){
          // update Impact's header count bubble + drawer contents from the rendered
          // sections — IN PLACE, so the theme's own element references stay intact.
          if(res && res.sections){
            if(headerId && res.sections[headerId]){
              if(!updateInPlace(res.sections[headerId],"cart-count")) updateInPlace(res.sections[headerId],".cart-count");
            }
            if(drawerId && res.sections[drawerId]) updateInPlace(res.sections[drawerId],"cart-drawer");
          }
          // notify the theme + any other cart listeners (covers Impact's pub/sub variants)
          ["cart:refresh","cart:updated","cart:change"].forEach(function(name){
            document.documentElement.dispatchEvent(new CustomEvent(name,{bubbles:true}));
          });
          document.dispatchEvent(new CustomEvent("tilt:added"));
          // best-effort: reveal the cart so the customer sees the item land
          try{
            var drawer = document.querySelector("cart-drawer");
            if(drawer){
              if(typeof drawer.show==="function") drawer.show();
              else if(typeof drawer.open==="function") drawer.open();
              else drawer.setAttribute("open","");
            }
          }catch(e){}
          refreshOwnCount();
          btn.classList.add("is-added");
          if(label) label.textContent = "Added ✓";
          if(live) live.textContent = "Added to cart";   // announce to assistive tech
          clearTimeout(resetTimer);
          resetTimer = setTimeout(function(){ btn.classList.remove("is-added"); if(label) label.textContent = orig || "Add to cart"; if(btn) btn.disabled=false; },1600);
        })
        .catch(function(err){
          // Shopify sets `description` on ANY add-to-cart failure (inventory, invalid
          // variant, throttle…), so the old code mislabelled 429s/network blips "Sold out".
          // Surface Shopify's own message when we have it; otherwise a neutral retry.
          var msg = (err && err.description) ? String(err.description) : "Try again";
          if(msg.length > 48) msg = msg.slice(0,47) + "…";
          if(label) label.textContent = msg;
          if(live) live.textContent = "Could not add to cart. " + msg;
          clearTimeout(resetTimer);
          resetTimer=setTimeout(function(){ if(label) label.textContent = orig || "Add to cart"; if(btn) btn.disabled=false; },1800);
        });
    });
  }

  /* ---------- Boot ---------- */
  /* ---------- Preloader (GRANDEUR logo veil) ---------- */
  function dismissPreloaders(){
    // querySelectorAll, NOT getElementById: duplicate/re-rendered sections can leave
    // more than one veil, and every single one must lift or the page stays black
    document.querySelectorAll(".tilt-world .preloader").forEach(function(pre){
      var bar = pre.querySelector(".preloader__bar span");
      if(bar) bar.style.width = "100%";
      setTimeout(function(){ pre.classList.add("done"); },380);
    });
  }
  function initPreloader(){
    var pres = document.querySelectorAll(".tilt-world .preloader:not(.done)");
    if(!pres.length) return;
    // theme editor: the veil is markup-gated behind request.design_mode too, but if
    // one ever renders there, dismiss it instantly — never black out the customizer
    if(window.Shopify && Shopify.designMode){ dismissPreloaders(); return; }
    pres.forEach(function(pre){
      var bar = pre.querySelector(".preloader__bar span");
      var prog = 0;
      var tick = setInterval(function(){ prog=Math.min(100,prog+Math.random()*20); if(bar)bar.style.width=prog+"%"; if(prog>=100)clearInterval(tick); },130);
    });
    window.addEventListener("load",dismissPreloaders);
    setTimeout(dismissPreloaders,4000);  // safety net if 'load' already fired / is slow
    if(document.readyState==="complete") dismissPreloaders();
  }

  function boot(){
    if(!document.querySelector(".tilt-world")) return;
    initPreloader();
    initProgress();
    initAnchors();
    initDepthParallax();
    initColourwayImages();
    initCart();
    // Theme editor: skip GSAP + Lenis entirely. Lenis attaches to window and per-scene
    // ScrollTriggers pin section DOM — after a section re-render (shopify:section:load)
    // they'd leak and fight the fresh DOM. The editor preview doesn't need the scroll
    // flourishes; the CSS sticky slide still shows the design.
    if(window.Shopify && Shopify.designMode) return;
    if(prefersReduced) return;
    // Touch devices (esp. Android): SKIP GSAP + Lenis entirely.
    //  • Lenis smooth-scroll fights native touch scroll → jank / stuck scroll.
    //  • GSAP sets inline transforms on the 9 sticky full-screen cards, keeping them
    //    GPU-promoted; pinch-zoom then re-rasters all of them and can CRASH the tab.
    // The card-stack is pure CSS position:sticky, so it still works without JS — touch
    // users just don't get the scroll scale/parallax flourishes (worth it to not crash).
    var isTouch = (navigator.maxTouchPoints||0) > 0 || window.matchMedia("(pointer:coarse)").matches;
    if(isTouch) return;
    Promise.all([
      loadScript("https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js",
                 "sha384-g4NTh/Iv5PPU4xPyhEWqPcwtNXOvdaDI8LLnyYfyNZOjKJeYQyjzQ9X5275eBjpt"),
      loadScript("https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js",
                 "sha384-Z3REaz79l2IaAZqJsSABtTbhjgOUYyV3p90XNnAPCSHg3EMTz1fouunq9WZRtj3d"),
      loadScript("https://cdn.jsdelivr.net/npm/@studio-freight/lenis@1.0.42/dist/lenis.min.js",
                 "sha384-li4UtcFCH6QeUqR4JyV58/VgTprMuz9aauj+oWtew7V4Y7ZVjnvz5px9Y3UCG0Ea")
    ]).then(function(){
      var lenis=null;
      if(typeof Lenis!=="undefined"){
        lenis=new Lenis({lerp:0.1,smoothWheel:true});
        window.__tiltLenis=lenis;
      }
      var gsapDrivesLenis = initGSAP(lenis);
      if(lenis && !gsapDrivesLenis){ var raf=function(t){lenis.raf(t);requestAnimationFrame(raf);}; requestAnimationFrame(raf); }
    }).catch(function(){});
  }

  if(document.readyState==="loading") document.addEventListener("DOMContentLoaded",boot); else boot();

  // Theme editor lifecycle: a setting change re-renders the section's DOM without
  // re-executing this script. Re-run the (idempotent) inits for the fresh markup and
  // make sure no freshly-rendered preloader veil is ever left covering the preview.
  document.addEventListener("shopify:section:load", function(){
    dismissPreloaders();
    initProgress();
    initAnchors();
    initDepthParallax();
    initColourwayImages();
    initCart();
  });
})();
